/**
 * O pareamento dos NÍVEIS DE PREÇO — a única definição do sistema.
 *
 * ## Por que isto mora em `shared` e não no parser
 *
 * O pareamento `del_3 = comissão 7% / DEL 5%` é conhecimento de **domínio**, e
 * quem precisa dele não é só quem lê planilha: a API monta o rótulo do seletor,
 * e o front monta o rótulo da trilha de auditoria. Enquanto ele morou só no
 * parser, o front escreveu a própria versão — e escreveu **errada**, derivando
 * o percentual do número que está no nome da chave.
 *
 * O resultado era `del_3` → "3%", quando `del_3` é **7%**. Os seis erravam, e o
 * único que tinha caso especial (`del_55`) errava também, porque trocava o par:
 * devolvia "5,5%" para o nível de 5% e "5%" para o de 5,5%.
 *
 * ## ⚠️ Os nomes `del_3`…`del_7` MENTEM, e é de propósito
 *
 * Eles são **chaves posicionais opacas**: `del_3` significa "a 1ª coluna, a mais
 * barata", e o número é resíduo histórico. Renomeá-los quebraria dado emitido —
 * `quote_item.del_nivel` e `product_price.price_source_key` os guardam em
 * orçamentos imutáveis.
 *
 * É por isso que o rótulo **não pode** ser derivado do nome, e é por isso que
 * esta tabela existe.
 *
 * ## As chaves `com_*` são o oposto — e não entram nesta tabela
 *
 * `com_5_0`…`com_2_0` foram desenhadas para **dizer a verdade**: o número É a
 * comissão. Elas são derivadas do próprio nome, sem tabela — e a função
 * `rotuloDeNivel` trata as duas famílias.
 */
/**
 * Os seis níveis, na ordem das colunas do documento oficial (a 1ª é a mais
 * barata).
 *
 * A comissão **desce** enquanto o del credere **sobe** — menos comissão ao
 * representante, mais caro o produto. Foi essa invariante que denunciou a
 * versão espelhada que o sistema carregou até 28/08/2026.
 */
export const PARES_DEL_CREDERE = [
    { fonte: 'del_3', comissao: '0.07', delCredere: '0.05' },
    { fonte: 'del_4', comissao: '0.06', delCredere: '0.10' },
    { fonte: 'del_5', comissao: '0.055', delCredere: '0.15' },
    { fonte: 'del_55', comissao: '0.05', delCredere: '0.20' },
    { fonte: 'del_6', comissao: '0.04', delCredere: '0.25' },
    { fonte: 'del_7', comissao: '0.03', delCredere: '0.30' },
];
/**
 * `'0.055'` vira `'5,5%'` — o percentual como o cliente o escreve.
 *
 * Sem `Intl`: a saída tem de ser idêntica no servidor e no navegador, e o
 * separador decimal do `toLocaleString` depende do ambiente.
 */
export function pctRotulo(fracao) {
    const n = Number(fracao) * 100;
    return `${n.toFixed(2).replace(/0+$/, '').replace(/[.,]$/, '').replace('.', ',')}%`;
}
/** `'com_4_5'` → `4.5`. `undefined` quando o nome não segue a forma. */
export function pctDaComissao(fonte) {
    const m = /^com_(\d+)_(\d+)$/.exec(fonte);
    if (m === null)
        return undefined;
    const n = Number(`${m[1] ?? ''}.${m[2] ?? ''}`);
    return Number.isFinite(n) ? n : undefined;
}
/**
 * O rótulo legível de um nível de preço, para qualquer das duas famílias.
 *
 * ```
 * del_3    → "7% · DEL 5%"      (pela tabela — o nome mente)
 * com_4_5  → "4,5%"             (pelo próprio nome — ele diz a verdade)
 * ```
 *
 * Nível desconhecido devolve a **própria chave**, nunca um percentual
 * inventado: mostrar o código é feio, mostrar o número errado é mentira — e
 * mentira sobre margem é exatamente o defeito que esta função veio consertar.
 */
export function rotuloDeNivel(fonte) {
    if (fonte === null || fonte === undefined || fonte === '')
        return undefined;
    const par = PARES_DEL_CREDERE.find((p) => p.fonte === fonte);
    if (par !== undefined) {
        return `${pctRotulo(par.comissao)} · DEL ${pctRotulo(par.delCredere)}`;
    }
    const pct = pctDaComissao(fonte);
    if (pct !== undefined)
        return pctRotulo(String(pct / 100));
    return fonte;
}
//# sourceMappingURL=niveis-preco.js.map